import { Button, FormControl, FormHelperText, FormLabel, Icon, useToast } from "@chakra-ui/react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { FaSave } from "react-icons/fa";
import { PhoneNumberDto, useUpdateOrganizationMutation } from "../../../../autogen/bff-api";
import { useLoggedInWithOrgContextState } from "../../../../common/auth/useLoggedInWithOrgContextState";
import { useApiError } from "../../../../common/errors/useApiError";
import {
  PhoneNumberInput,
  PhoneNumberInput as PhoneNumberInputType,
} from "../../../../common/input/PhoneNumberInput";
import { useAppDispatch } from "../../../../common/redux/hooks";
import { updateSelectedOrg } from "../../../../common/redux/reducers/authStateReducer";

interface Props {
  currentValue: PhoneNumberDto | null;
}

function toInputType(dto: PhoneNumberDto | null): PhoneNumberInputType | null {
  if (!dto) return null;
  return {
    country: dto.callingCode
      ? {
          name: dto.callingCode.countryName,
          alpha3Code: dto.callingCode.countryAlpha3Code,
          callingCode: dto.callingCode.value,
        }
      : null,
    number: dto.number,
  };
}

export const ChangeContactPhone = ({ currentValue }: Props) => {
  const toast = useToast();
  const authState = useLoggedInWithOrgContextState();
  const [editOrg, { isLoading }] = useUpdateOrganizationMutation();
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const displayer = useApiError();
  const initialValue = toInputType(currentValue);
  const [phoneValue, setPhoneValue] = useState<PhoneNumberInputType | null>(initialValue);

  const hasChanged = JSON.stringify(initialValue) !== JSON.stringify(phoneValue);

  const save = async () => {
    const response = await editOrg({
      orgId: authState.selectedOrg.id,
      editOrganizationRequest: {
        contactPhone: {
          value:
            phoneValue?.country && phoneValue?.number
              ? {
                  callingCode: {
                    countryAlpha3Code: phoneValue.country.alpha3Code,
                    countryName: phoneValue.country.name,
                    value: phoneValue.country.callingCode,
                  },
                  number: phoneValue.number,
                }
              : undefined,
        },
      },
    });
    if ("data" in response) {
      dispatch(updateSelectedOrg(response.data));
      toast({ title: t("Contact phone has been updated!"), status: "success" });
    } else {
      displayer.trigger(response.error);
    }
  };

  return (
    <FormControl mt="15px">
      <FormLabel>{t("Contact phone")}</FormLabel>
      <PhoneNumberInput value={phoneValue} onChange={(val) => setPhoneValue(val)} />
      <FormHelperText>{t("Please provide a contact phone number for your company")}</FormHelperText>
      {hasChanged && (
        <Button
          mt="10px"
          leftIcon={<Icon as={FaSave} w="15px" h="15px" mt="-2px" />}
          variant={"solid"}
          colorScheme="teal"
          size={"sm"}
          isLoading={isLoading}
          onClick={save}
        >
          {t("Save change")}
        </Button>
      )}
    </FormControl>
  );
};
